import {
  Box,
  FormHelperText,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { Controller, FieldValues } from 'react-hook-form';
import { FormComponentProps } from './FormComponentProps';

export interface SelectionCardOption {
  value: string;
  label: string;
  hint?: string | null;
  tag?: string | null;
  disabled?: boolean;
  disabledReason?: string | null;
}

interface Props<TFieldValues> extends FormComponentProps<TFieldValues> {
  options: SelectionCardOption[];
  required?: boolean;
  columns?: number;
}

export const FormSelectionCard = <TFieldValues extends FieldValues>({
  name,
  control,
  label,
  className,
  options,
  required,
  disabled,
  columns = 2,
}: Props<TFieldValues>) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState: { error } }) => (
      <Box className={className}>
        <FormLabel
          component="label"
          error={!!error}
          sx={required && !error ? { color: 'black', fontWeight: 'bold' } : undefined}
        >
          {label} {required ? '*' : ''}
        </FormLabel>

        <RadioGroup
          {...field}
          value={field.value ?? ''}
          onChange={(_event, value) => {
            const option = options.find((o) => o.value === value);
            if (disabled || !option || option.disabled) return;
            field.onChange(value);
          }}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: `repeat(${columns}, 1fr)` },
            gap: 1.5,
            mt: 1,
          }}
        >
          {options.map((option) => {
            const isDisabled = disabled || option.disabled;
            const isSelected = field.value === option.value;

            return (
              <Paper
                key={option.value}
                component="label"
                variant="outlined"
                sx={{
                  display: 'block',
                  p: 1.5,
                  m: 0,
                  opacity: isDisabled ? 0.55 : 1,
                  borderColor: isSelected ? 'primary.main' : undefined,
                  borderWidth: isSelected ? 2 : 1,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <Stack direction="row" alignItems="flex-start" gap={1}>
                  <Radio
                    value={option.value}
                    checked={isSelected}
                    disabled={isDisabled}
                    inputProps={{ 'aria-label': option.label }}
                    sx={{ p: 0.25, mt: '2px' }}
                  />
                  <Stack flexGrow={1} gap={0.25}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography fontWeight={600} flexGrow={1}>
                        {option.label}
                      </Typography>
                      {option.tag && (
                        <Typography
                          variant="caption"
                          sx={{
                            px: 0.75,
                            py: 0.125,
                            border: '1px solid',
                            borderColor: 'divider',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {option.tag}
                        </Typography>
                      )}
                    </Stack>
                    {option.hint && (
                      <Typography variant="body2" color="text.secondary">
                        {option.hint}
                      </Typography>
                    )}
                    {isDisabled && option.disabledReason && (
                      <Typography variant="body2" color="text.secondary">
                        {option.disabledReason}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </RadioGroup>

        <FormHelperText error={!!error}>{error ? error.message : ''}</FormHelperText>
      </Box>
    )}
  />
);
